import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import FilterListIcon from '@material-ui/icons/FilterList';
import RefreshButton from '@material-ui/icons/Refresh';
import { clsx } from 'clsx';
import PropTypes from 'prop-types';
import React, { Component, forwardRef } from 'react';
import DataDisplay from './DataDisplay';
import FilterEditor from './FilterEditor';
import { filterTypes } from './filter-utils';

import DataTableToolbar from './DataTableToolbar';
import QueryBox from './QueryBox';
/**
 * Componente que renderiza un datatable con capacidad de filtrado, ordenado y paginación
 * de elementos. Además de permitir incorporar acciones a realizar por registro.
 */
class DataTable extends Component {
  state = {
    disableReload: false,
    addingFilters: false,
    selectionFilterValues: {},
    defaultFilters: []
  };

  constructor(props) {
    super(props);
    const { settings, defaultFilters, disableReload } = props;
    const supportsFilters = Object.keys(settings).reduce(
      (acc, current) => acc || settings[current].filterable,
      false
    );
    this.state = {
      disableReload,
      addingFilters: false,
      supportsFilters,
      selectionFilterValues: {},
      defaultFilters
    };
  }

  onFilterCancel() {
    this.setState({ addingFilters: false });
  }

  onFilterRequested(baseFilter) {
    this.setState({ addingFilters: true, baseFilter });
  }

  componentDidMount() {
    this.actualizar(this.state.defaultFilters ? this.state.defaultFilters : []);
    this.props.resetDataTable();
    this.props.onReloadTable();
  }

  // Método público que puede ser accedido mediante la referencia
  reload = () => {
    this.actualizar(this.state.defaultFilters ? this.state.defaultFilters : []);
    this.props.resetDataTable();
    this.props.onReloadTable();
  };

  actualizar(filters) {
    const { settings } = this.props;
    let optionsFromSettings;
    const actual = {};
    const filtros = filters.concat(this.props.dataTableState.queryData);
    if (filtros) {
      let i;
      for (i = 0; i < filtros.length; i += 1) {
        const filter = filtros[i];
        if (filter.type === filterTypes.SELECTION) {
          optionsFromSettings = settings[filter.key].selectOptions;
          if (optionsFromSettings.then) {
            optionsFromSettings.then(options => {
              actual[filter.key] = options;
              this.setState({ selectionFilterValues: actual });
            });
          } else {
            actual[filter.key] = optionsFromSettings;
            this.setState({ selectionFilterValues: actual });
          }
        }
      }
    }
  }

  render() {
    const { props } = this;
    const { classes } = props;
    const {
      supportsFilters,
      addingFilters,
      baseFilter,
      disableReload
    } = this.state;
    return (
      <div className={classes.dataTableContainer}>
        {addingFilters && (
          <FilterEditor
            baseFilter={baseFilter}
            settings={props.settings}
            onFilterRemove={filter => props.onFilterRemove(filter)}
            onFilterAdd={filters => {
              props.onFilterAdd(filters);
              this.actualizar(filters);
              // props.resetPaging();
              props.resetDataTable();
              props.onReloadTable();
            }}
            onCancel={() => this.onFilterCancel()}
          />
        )}
        <Paper
          className={clsx(classes.paper, {
            [classes.error]: props.error
          })}
        >
          {props.dataTableState.itemsData.loading && (
            <LinearProgress color="secondary" />
          )}
          {!props.dataTableState.itemsData.loading && (
            <div style={{ height: '4px', backgroundColor: 'white' }} />
          )}

          {(props.title ||
            props.description ||
            props.toolbarActions ||
            !disableReload ||
            supportsFilters ||
            props.toolbarActionsEnd) && (
            <DataTableToolbar
              title={props.title}
              description={props.description || ''}
            >
              {props.toolbarActions}

              {!disableReload && (
                <Tooltip title="Recargar">
                  <IconButton
                    onClick={() => {
                      props.resetItemsData();
                      props.onReloadTable();
                    }}
                  >
                    <RefreshButton />
                  </IconButton>
                </Tooltip>
              )}
              {supportsFilters && (
                <Tooltip title="Agregar filtro">
                  <IconButton onClick={() => this.onFilterRequested()}>
                    <FilterListIcon />
                  </IconButton>
                </Tooltip>
              )}
              {props.toolbarActionsEnd}
            </DataTableToolbar>
          )}

          {supportsFilters && (
            <QueryBox
              settings={props.settings}
              filters={props.dataTableState.queryData}
              onFilterRequested={filter => this.onFilterRequested(filter)}
              onFilterAdd={filters => {
                props.onFilterAdd(filters);
                // props.resetPaging();
                props.resetDataTable();
                props.onReloadTable();
              }}
              onFilterRemove={filter => {
                props.onFilterRemove(filter);
                // props.resetPaging();
                props.resetDataTable();
                props.onReloadTable();
              }}
              selectionFilterValues={this.state.selectionFilterValues}
            />
          )}
          <DataDisplay
            listData={props.dataTableState.itemsData}
            orderData={props.dataTableState.orderData}
            pagerData={props.dataTableState.pagerData}
            onItemClick={props.onItemClick}
            onPageChanged={page => {
              props.resetItemsData();
              props.onPageChanged(page);
              props.onReloadTable();
            }}
            onChangeItemsCountPerPage={itemsCountPerPage => {
              props.resetItemsData();
              props.onChangeItemsCountPerPage(itemsCountPerPage);
              props.onReloadTable();
            }}
            settings={props.settings}
            onOrderChanged={key => {
              props.resetItemsData();
              props.onOrderChanged(key);
              props.onReloadTable();
            }}
            filters={props.dataTableState.queryData}
            fileName={props.fileName}
          />
        </Paper>
      </div>
    );
  }
}

DataTable.propTypes = {
  /**
   * Título del DataTable.
   */
  title: PropTypes.string.isRequired,

  /**
   * Función que permite cargar los datos del datatable.
   */
  onReloadTable: PropTypes.func.isRequired,

  /**
   * Función que permite reiniciar el estado del datatable (solo paginacion y datos de item)
   */
  resetDataTable: PropTypes.func.isRequired,

  /**
   * Función que permite reiniciar el estado de los items
   */
  resetItemsData: PropTypes.func.isRequired,

  /**
   * Objeto que almacena el estado del datatable.
   */
  dataTableState: PropTypes.shape({
    /**
     * Listado de registros a mostrar en la tabla. Prop recibido por
     * el componente [DataDisplay](#datadisplay).
     */
    itemsData: PropTypes.object,

    /**
     * Configuración de ordenamiento.
     */
    orderData: PropTypes.object,

    /**
     * Configuración de paginación.
     */
    pagerData: PropTypes.object,

    /**
     * Objeto que contiene los filtros aplicados al datatable. Utilizado
     * por el componente [QueryBox](#querybox)
     */
    queryData: PropTypes.array
  }).isRequired,

  /**
   * Función invocada cuando se hace click en los botones de paginación.
   *
   * @param {number} page - El número de página.
   */
  onPageChanged: PropTypes.func.isRequired,

  /**
   * Se utiliza para la inicializacion de los componentes [DataTableRow](#datatablerow) y
   * [DataTableHeader](#datatableheader)
   */
  settings: PropTypes.object.isRequired,

  /**
   * Función para agregar nuevos filtros
   */
  onFilterAdd: PropTypes.func.isRequired,

  /**
   * Funcion para eliminar un filtro
   */
  onFilterRemove: PropTypes.func.isRequired,

  /**
   * Función que restablece los datos de paginación.
   */
  resetPaging: PropTypes.func.isRequired,

  /**
   * Función invocada cuando se hace click sobre una columna "ordenable".
   *
   * @param {string} field - La columna afectada.
   */
  onOrderChanged: PropTypes.func.isRequired,

  /**
   * Array de elementos que se renderizaran en el toolbar como acciones del datatable
   */
  toolbarActions: PropTypes.arrayOf(PropTypes.element)
};

const DataTableComponent = forwardRef((propsComponent, ref) => {
  // Crear instancia del componente
  const instance = React.useRef(null);

  // Adjuntar métodos públicos al ref usando forwardRef
  React.useImperativeHandle(ref, () => ({
    reload: () => instance.current.reload()
  }));

  return <DataTable {...propsComponent} ref={instance} />;
});

const styleSheet = theme => ({
  dataTableContainer: {
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 'auto'
  },
  paper: {
    width: '100%',
    borderRadius: `0 0 ${theme.spacing(0.5, 0.5)}`,
    border: '0'
  },
  error: {
    border: `1px ${theme.palette.error.main} solid`
  }
});

export default withStyles(styleSheet)(DataTableComponent);
